/**
 * @file Axios请求封装
 * @description 封装请求拦截器（添加token）、响应拦截器（统一处理错误、401跳转登录）
 */

import axios from 'axios'
import { Message } from 'element-ui'
import store from '../store'
import router from '../router'
import config from '@/config'

// 判断是否为本地开发环境（走 webpack proxy）
const isLocalDev = process.env.NODE_ENV === 'local' || process.env.NODE_ENV === 'development'

// 创建axios实例
const service = axios.create({
  // local/dev 环境使用相对路径走 proxy，生产环境使用绝对 URL
  baseURL: isLocalDev ? '/api/v1' : config.apiBaseURL,
  timeout: config.requestTimeout || 15000 // 请求超时时间
})

// 请求拦截器 - 添加token到请求头
service.interceptors.request.use(
  config => {
    // 从localStorage或vuex获取token
    const token = store.state.user.token || localStorage.getItem('token')
    if (token) {
      // 添加token到Authorization请求头，格式为Bearer token
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  error => {
    // 请求错误处理
    console.error('Request error:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器 - 统一处理响应错误
service.interceptors.response.use(
  response => {
    const res = response.data
    
    // 检查后端服务器的响应格式
    if (res.success) {
      // 请求成功，直接返回数据
      return res.data
    } else {
      // 请求失败，显示错误提示
      Message({
        message: res.message || '请求失败，请稍后重试',
        type: 'error',
        duration: 5000
      })
      
      // 401表示未授权（token过期或无效），需要跳转登录页
      if (res.error === 'Unauthorized' || res.code === -401 || res.code === 401) {
        // 清除用户信息并跳转到登录页面
        store.dispatch('user/logout')
        router.push(`/login?redirect=${router.currentRoute.fullPath}`)
      }
      
      return Promise.reject(new Error(res.message || '请求失败'))
    }
  },
  error => {
    console.error('Response error:', error)
    let message = error.message || '网络错误，请检查网络连接后重试'
    
    // 处理HTTP错误状态码
    if (error.response) {
      const { status } = error.response
      
      switch (status) {
        case 401:
          // 未授权，清除用户信息跳转登录
          message = '登录已过期，请重新登录'
          store.dispatch('user/logout')
          router.push(`/login?redirect=${router.currentRoute.fullPath}`)
          break
        case 403:
          message = '没有权限访问该资源'
          break
        case 404:
          // 静默处理 404，让调用方自行处理（如显示空状态或模拟数据）
          message = null
          break
        case 500:
          message = '服务器内部错误，请稍后重试'
          break
        case 502:
          message = '网关错误，请稍后重试'
          break
        case 503:
          message = '服务不可用，请稍后重试'
          break
        default:
          message = `请求错误(${status})`
      }
    } else if (error.request) {
      // 请求发出但没有收到响应
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        message = '请求超时，请稍后重试'
      } else {
        message = '无法连接到服务器，请检查网络'
      }
    }
    
    // 显示错误提示（message 为 null 时静默处理）
    if (message) {
      Message({
        message,
        type: 'error',
        duration: 5000
      })
    }
    
    return Promise.reject(error)
  }
)

export default service
